import { z } from 'zod';

/** `GET /api/admin/workspaces?page=` / `GET /api/admin/bots?page=` query (spec
 * docs/superpowers/specs/2026-08-06-admin-platform-design.md §2/§3) — page-NUMBER
 * pagination, unlike the customer-facing cursor-paginated list routes. No `perPage`: page
 * size is fixed at 50 server-side (`createAdminRepos`, `@repo/db`) since only ops staff hit
 * these and a fixed size keeps "page N" stable across requests. */
export const adminPageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
});

export type AdminPageQuery = z.infer<typeof adminPageQuerySchema>;

/** `POST /api/admin/workspaces/:id/internal` body (admin v2 / internal workspaces, spec
 * 2026-08-06-admin-v2-internal-workspaces-design.md §A). No partial/omitted form — the
 * toggle always states the full desired value, so the route (and any retry of the same
 * request) is idempotent by construction. */
export const setWorkspaceInternalSchema = z.object({
  internal: z.boolean(),
});

export type SetWorkspaceInternalInput = z.infer<typeof setWorkspaceInternalSchema>;

// ── Admin v2 (spec docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B) ──

/** `GET /api/admin/jobs?status=&page=` query. Both fields are optional with defaults so the
 * schema can be reused for the "status or page present" branch of `routes/admin.ts`'s
 * `GET /api/admin/jobs` handler — the legacy no-query-params shape is detected BEFORE this
 * schema ever runs (see that route's doc comment). `status` is deliberately the 3-way tab
 * enum (spec §B3: "All|Running|Failed"), not the full `JobStatus` union. */
export const adminJobsQuerySchema = z.object({
  status: z.enum(['all', 'running', 'failed']).default('all'),
  page: z.coerce.number().int().min(1).default(1),
});

export type AdminJobsQuery = z.infer<typeof adminJobsQuerySchema>;

/** `POST /api/admin/users/:id/ban` body (spec §B4). Reason is optional — an admin can ban
 * without typing one; `banReason` on the `User` row is simply `null` in that case. Trimmed +
 * bounded so an accidental giant paste doesn't land in the DB verbatim. */
export const banUserSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});

export type BanUserInput = z.infer<typeof banUserSchema>;

/** `POST /api/admin/users/:id/role` body (spec §B4) — only the two roles this codebase's
 * `requireAdmin` gate and better-auth `admin()` plugin config actually recognize (see
 * `apps/api/src/lib/auth.ts`'s comment on `adminRoles`). */
export const setUserRoleSchema = z.object({
  role: z.enum(['user', 'admin']),
});

export type SetUserRoleInput = z.infer<typeof setUserRoleSchema>;

/** `GET /api/admin/feedback?handled=all|false&page=` query (feedback-form design,
 * docs/superpowers/specs/2026-08-18-feedback-form-design.md). Same page-number pagination as
 * `adminJobsQuerySchema` above, but only two `handled` states exist (spec: "single handled
 * boolean, no pipeline, no assignment") — unlike `adminJobsQuerySchema`'s three-way status
 * tab, there's no `'true'` tab: an admin looking at "just handled" reports isn't a triage use
 * case this screen serves. */
export const adminFeedbackQuerySchema = z.object({
  handled: z.enum(['all', 'false']).default('all'),
  page: z.coerce.number().int().min(1).default(1),
});

export type AdminFeedbackQuery = z.infer<typeof adminFeedbackQuerySchema>;
